import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlayIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import {
  saveCustomReport,
  exportCustomReport,
  generateCustomReport,
} from "../../../api/customReportApi";
import { useReportBuilderStore } from "./store";
import DateFormatSelect from "../dialogs/DateFormatSelect";
import { DEFAULT_DATE_FORMAT_KEY } from "../../../utils/dateFormatCatalog";
import { validateReportFormulas } from "./FormulaEditor";

function SettingsPopover({ onClose }: { onClose: () => void }) {
  const {
    config,
    setVisibility,
    setReportDescription,
    setMaskPii,
    setDateFormat,
  } = useReportBuilderStore();

  return (
    <div className="absolute right-0 top-10 z-50 w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
        <span className="text-xs font-semibold text-gray-700">
          Report Settings
        </span>
        <button onClick={onClose}>
          <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </button>
      </div>

      <div className="px-3 py-3 flex flex-col gap-3">
        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description
          </label>
          <textarea
            value={config.description}
            onChange={(e) => setReportDescription(e.target.value)}
            rows={2}
            placeholder="What does this report show?"
            className="w-full text-xs px-2.5 py-1.5 border border-inputBorder rounded-md focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Visibility
          </label>
          <div className="flex gap-1">
            {(["private", "team"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVisibility(v)}
                className={`flex-1 py-1.5 text-[11px] font-medium border rounded-md capitalize transition-colors ${
                  config.settings.visibility === v
                    ? "bg-orange-50 border-primary text-primary"
                    : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Mask PII */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-700">Mask PII Data</p>
            <p className="text-[11px] text-gray-400">
              Hides name, email, PAN, contact
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMaskPii(!config.settings.maskPii)}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
              config.settings.maskPii ? "bg-primary" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                config.settings.maskPii ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Date Format */}
        <div>
          <DateFormatSelect
            value={config.settings.dateFormat ?? DEFAULT_DATE_FORMAT_KEY}
            onChange={setDateFormat}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Export menu ──────────────────────────────────────────────────────────────

function ExportMenu({
  onClose,
  reportId,
  reportName,
  maskPii,
}: {
  onClose: () => void;
  reportId: string | undefined;
  reportName: string;
  maskPii: boolean;
}) {
  const [loading, setLoading] = useState<"generate" | "direct" | null>(null);
  const [queuedMsg, setQueuedMsg] = useState<string | null>(null);

  async function handleGenerate() {
    if (!reportId) {
      toast(
        reportName.trim()
          ? "Save this named report before generating it."
          : "Name the report before saving and generating it.",
        {
          type: "warning",
          autoClose: 2500,
        }
      );
      return;
    }
    setLoading("generate");
    try {
      const result = await generateCustomReport(reportId, { maskPii });
      setQueuedMsg(result.message);
      setTimeout(() => {
        setQueuedMsg(null);
        onClose();
      }, 3000);
    } catch {
      toast("Failed to queue the report. Please try again.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(null);
    }
  }

  async function handleDirectDownload() {
    if (!reportId) {
      toast(
        reportName.trim()
          ? "Save this named report before downloading it."
          : "Name the report before saving and downloading it.",
        {
          type: "warning",
          autoClose: 2500,
        }
      );
      return;
    }
    setLoading("direct");
    try {
      const { base64, contentType, fileName } = await exportCustomReport(
        reportId
      );
      const blob = new Blob(
        [Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))],
        { type: contentType }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch {
      toast("Export failed. Please try again.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(null);
    }
  }

  if (queuedMsg) {
    return (
      <div className="absolute right-0 top-10 z-50 w-64 bg-white border border-borderColor rounded-xl shadow-xl p-3">
        <div className="flex items-start gap-2">
          <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-primaryText">{queuedMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute right-0 top-10 z-50 w-56 bg-white border border-borderColor rounded-xl shadow-xl overflow-hidden">
      <button
        onClick={handleGenerate}
        disabled={loading !== null}
        className="w-full px-3 py-2.5 text-xs text-left hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 border-b border-borderColor"
      >
        <PlayIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <div>
          <div className="font-medium text-primaryText">
            {loading === "generate" ? "Queueing…" : "Generate Report"}
          </div>
          <div className="text-[10px] text-secondaryText">
            Queued — download when ready
          </div>
        </div>
      </button>
      <button
        onClick={handleDirectDownload}
        disabled={loading !== null}
        className="w-full px-3 py-2.5 text-xs text-left hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
      >
        <ArrowDownTrayIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <div>
          <div className="font-medium text-primaryText">
            {loading === "direct" ? "Downloading…" : "Download Now"}
          </div>
          <div className="text-[10px] text-secondaryText">
            Instant Excel download
          </div>
        </div>
      </button>
    </div>
  );
}

// ─── Template menu ────────────────────────────────────────────────────────────

// Fields that must exist in an imported template
const REQUIRED_TEMPLATE_KEYS = ["name", "sheets", "settings"] as const;

function isValidTemplate(obj: unknown): obj is Record<string, unknown> {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj))
    return false;
  return REQUIRED_TEMPLATE_KEYS.every(
    (k) => k in (obj as Record<string, unknown>)
  );
}

function TemplateMenu({ onClose }: { onClose: () => void }) {
  const { config, loadReport } = useReportBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleExport() {
    // Strip the saved ID so the import creates a fresh report, not an overwrite
    const { id: _id, ...exportable } = config;
    const blob = new Blob([JSON.stringify(exportable, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(config.name || "report").replace(
      /\s+/g,
      "_"
    )}_template.json`;
    a.click();
    URL.revokeObjectURL(url);
    onClose();
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const raw = JSON.parse(ev.target?.result as string);

        if (!isValidTemplate(raw)) {
          toast(
            "Invalid template file: missing required fields (name, sheets, settings).",
            {
              type: "error",
              autoClose: 4000,
            }
          );
          return;
        }

        // Validate sheets array
        const sheets = raw.sheets as unknown[];
        if (!Array.isArray(sheets) || sheets.length === 0) {
          toast("Invalid template: sheets must be a non-empty array.", {
            type: "error",
            autoClose: 4000,
          });
          return;
        }

        for (const sheet of sheets) {
          if (
            typeof sheet !== "object" ||
            sheet === null ||
            !("name" in sheet) ||
            !("columns" in sheet)
          ) {
            toast(
              "Invalid template: each sheet must have a name and columns array.",
              {
                type: "error",
                autoClose: 4000,
              }
            );
            return;
          }
        }

        // Remove any saved ID so this loads as a new unsaved report
        delete (raw as any).id;

        loadReport(raw as any);
        toast(`Template "${raw.name}" loaded. Review and save when ready.`, {
          type: "success",
          autoClose: 3000,
        });
        onClose();
      } catch {
        toast(
          "Could not parse the file. Make sure it is a valid JSON template.",
          {
            type: "error",
            autoClose: 4000,
          }
        );
      } finally {
        // Reset so the same file can be re-selected if needed
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  }

  return (
    <div className="absolute right-0 top-10 z-50 w-52 bg-white border border-borderColor rounded-xl shadow-xl overflow-hidden">
      <button
        onClick={handleExport}
        className="w-full px-3 py-2.5 text-xs text-left hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-borderColor"
      >
        <DocumentArrowDownIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <div>
          <div className="font-medium text-primaryText">Export template</div>
          <div className="text-[10px] text-secondaryText">
            Download config as JSON
          </div>
        </div>
      </button>
      <button
        onClick={() => fileInputRef.current?.click()}
        className="w-full px-3 py-2.5 text-xs text-left hover:bg-gray-50 transition-colors flex items-center gap-2"
      >
        <DocumentArrowUpIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <div>
          <div className="font-medium text-primaryText">Import template</div>
          <div className="text-[10px] text-secondaryText">
            Load from a JSON file
          </div>
        </div>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleImport}
      />
    </div>
  );
}

// ─── Report Header ────────────────────────────────────────────────────────────

export function ReportHeader({ readOnly = false }: { readOnly?: boolean }) {
  const {
    config,
    isDirty,
    isSaving,
    isAdvancedMode,
    setAdvancedMode,
    setReportName,
    setIsSaving,
    markSaved,
    setReportId,
    undo,
    redo,
    history,
    historyIndex,
    lastSavedAt,
  } = useReportBuilderStore();

  const [showSettings, setShowSettings] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  async function handleSave() {
    if (readOnly) {
      toast("This shared report is view-only. Only its owner can edit it.", {
        type: "info",
        autoClose: 3000,
      });
      return;
    }
    if (!config.name.trim()) {
      nameInputRef.current?.focus();
      toast("Name the report before saving it.", {
        type: "warning",
        autoClose: 2500,
      });
      return;
    }
    const formulaError = validateReportFormulas(config);
    if (formulaError) {
      toast(`${formulaError} before saving.`, {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }
    setIsSaving(true);
    try {
      const { id } = await saveCustomReport(config);
      if (!config.id) setReportId(id);
      markSaved();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      toast("Failed to save report. Please try again.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200 flex-shrink-0">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
        title="Back to reports"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      {/* Report name */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <input
          ref={nameInputRef}
          type="text"
          value={config.name}
          onChange={(e) => setReportName(e.target.value)}
          readOnly={readOnly}
          className="text-sm font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary focus:outline-none px-0.5 py-0.5 transition-colors min-w-0 truncate max-w-xs"
          placeholder="Untitled Report"
          title="Click to rename"
        />
        {isDirty && (
          <span className="text-[10px] text-amber-600 font-medium bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex-shrink-0">
            Unsaved
          </span>
        )}
        {savedFlash && (
          <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium bg-green-50 border border-green-200 px-1.5 py-0.5 rounded flex-shrink-0">
            <CheckCircleIcon className="w-3 h-3" />
            Saved
          </span>
        )}
        {lastSavedAt && !isDirty && !savedFlash && (
          <span
            className="text-[10px] text-gray-400 flex-shrink-0"
            title={lastSavedAt.toLocaleString()}
          >
            Saved{" "}
            {lastSavedAt.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </div>

      {/* Undo / Redo */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={undo}
          disabled={!canUndo || readOnly}
          title="Undo (Ctrl+Z)"
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-gray-100 transition-colors"
        >
          <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo || readOnly}
          title="Redo (Ctrl+Y)"
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-gray-100 transition-colors"
        >
          <ArrowUturnRightIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="h-4 w-px bg-gray-200" />

      {/* Simple / Advanced mode toggle */}
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-gray-500">Simple</span>
        <button
          onClick={() => setAdvancedMode(!isAdvancedMode)}
          disabled={readOnly}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            isAdvancedMode ? "bg-primary" : "bg-gray-200"
          }`}
          title={
            isAdvancedMode
              ? "Switch to simple mode"
              : "Enable advanced mode (formulas, aggregations, grouping)"
          }
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
              isAdvancedMode ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
        <span className="text-[11px] text-gray-500">Advanced</span>
      </div>

      <div className="h-4 w-px bg-gray-200" />

      {/* Settings */}
      <div className="relative">
        <button
          disabled={readOnly}
          onClick={() => {
            setShowSettings((v) => !v);
            setShowExport(false);
          }}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Report settings"
        >
          <Cog6ToothIcon className="w-4 h-4" />
        </button>
        {showSettings && (
          <SettingsPopover onClose={() => setShowSettings(false)} />
        )}
      </div>

      {/* Export */}
      <div className="relative">
        <button
          onClick={() => {
            setShowExport((v) => !v);
            setShowSettings(false);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
          Export
        </button>
        {showExport && (
          <ExportMenu
            onClose={() => setShowExport(false)}
            reportId={config.id}
            reportName={config.name}
            maskPii={config.settings.maskPii}
          />
        )}
      </div>

      {/* Template */}
      <div className="relative">
        <button
          disabled={readOnly}
          onClick={() => {
            setShowTemplate((v) => !v);
            setShowExport(false);
            setShowSettings(false);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowUpTrayIcon className="w-3.5 h-3.5" />
          Template
        </button>
        {showTemplate && (
          <TemplateMenu onClose={() => setShowTemplate(false)} />
        )}
      </div>

      {/* Save */}
      <button
        data-tour="save-report"
        onClick={handleSave}
        disabled={isSaving || readOnly}
        className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primaryHover disabled:opacity-60 transition-colors"
      >
        {isSaving ? (
          <>
            <svg
              className="w-3.5 h-3.5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Saving…
          </>
        ) : (
          <>
            <CloudArrowUpIcon className="w-3.5 h-3.5" />
            Save report
          </>
        )}
      </button>
    </div>
  );
}
